import React, { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import CopyButton from './CopyButton'
import { InlinePill } from './DashboardPrimitives'
import { CryptoPayoutEntryResponse, FiatPayoutEntryResponse } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { getMethod } from '../util/Managed'
import { assetLine } from '../util/AssetLabels'
import { payoutStatusLabel, payoutStatusTone } from '../util/PayoutStatus'
import { roundAssetAmount, shortAddress } from '../util/Util'

const PayoutRecipientGrid = styled.div`
  border: 1px solid var(--surface-pressed, #edf1f7);
  border-radius: 8px;
  overflow: hidden;
`

const PayoutRecipientHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr 1fr 1fr;
  gap: 10px;
  padding: 6px 10px;
  background: var(--surface-muted, #f8fafc);
  border-bottom: 1px solid var(--surface-pressed, #edf1f7);
  color: #8a93a3;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const PayoutRecipientDataRow = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr 1fr 1fr;
  gap: 10px;
  padding: 9px 10px;
  border-top: 1px solid var(--surface-pressed, #edf1f7);
  align-items: center;

  &:first-of-type {
    border-top: none;
  }
`

const PayoutRecipientNum = styled.span`
  color: #9ca3af;
  font-size: 11px;
  line-height: 1;
`

const PayoutRecipientIdentifier = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const PayoutRecipientPrimary = styled.span`
  font-family: var(--font-mono);
  font-size: 11.5px;
  color: var(--ink, #20242d);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PayoutRecipientName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ink, #20242d);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PayoutRecipientSecondary = styled.span`
  font-size: 10.5px;
  color: var(--text-faint, #7b8494);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PayoutRecipientAmount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ink, #20242d);
  text-align: right;
  white-space: nowrap;
`

const PayoutRecipientStatus = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  min-width: 0;
`

const PayoutRecipientOverflowRow = styled.div`
  border-top: 1px solid var(--surface-pressed, #edf1f7);
  color: var(--text-faint, #7b8494);
  font-size: 12px;
  padding: 9px 10px;
  text-align: right;
`


const PayoutRecipients = ({ cryptoEntries, fiatEntries, viewAllHref, limit = 5 }: {
  cryptoEntries?: CryptoPayoutEntryResponse[]
  fiatEntries?: FiatPayoutEntryResponse[]
  viewAllHref: string
  limit?: number
}): ReactElement | null => {
  const crypto = cryptoEntries ?? []
  const fiat = fiatEntries ?? []
  const total = crypto.length + fiat.length
  if (total === 0) return null

  const displayCrypto = crypto.slice(0, limit)
  const displayFiat = fiat.slice(0, Math.max(0, limit - displayCrypto.length))
  const hiddenCount = total - displayCrypto.length - displayFiat.length
  const hasBothTypes = crypto.length > 0 && fiat.length > 0

  return <PayoutRecipientGrid>
    <PayoutRecipientHeaderRow>
      <span>#</span>
      <span>{hasBothTypes || fiat.length > 0 ? 'Recipient' : 'Address'}</span>
      <span style={{ textAlign: 'right' }}>Amount</span>
      <span style={{ textAlign: 'right' }}>Status</span>
    </PayoutRecipientHeaderRow>

    {displayCrypto.map((entry, idx) => {
      const method = getMethod(entry.network, entry.token)
      const identifier = entry.payoutAddress || entry.externalAccountId || '-'
      return <PayoutRecipientDataRow key={entry.id ?? idx}>
        <PayoutRecipientNum>{idx + 1}</PayoutRecipientNum>
        <PayoutRecipientIdentifier>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            <PayoutRecipientPrimary>{shortAddress(identifier, 6)}</PayoutRecipientPrimary>
            {identifier !== '-' && <span onClick={e => e.stopPropagation()}>
              <CopyButton text={identifier} />
            </span>}
          </div>
          <PayoutRecipientSecondary>
            {assetLine(method)}
          </PayoutRecipientSecondary>
        </PayoutRecipientIdentifier>
        <PayoutRecipientAmount>
          {roundAssetAmount(method.ticker, Number(entry.amount))} {method.ticker}
        </PayoutRecipientAmount>
        <PayoutRecipientStatus>
          <InlinePill $tone={payoutStatusTone(entry.status)}>{payoutStatusLabel(entry.status)}</InlinePill>
        </PayoutRecipientStatus>
      </PayoutRecipientDataRow>
    })}

    {displayFiat.map((entry, idx) => {
      const displayName = entry.beneficiaryName ?? entry.beneficiaryCompanyName
      const displayAccount = entry.iban ?? entry.sortCode ?? entry.bban ?? entry.externalAccountId
      const globalIdx = displayCrypto.length + idx
      return <PayoutRecipientDataRow key={entry.id ?? globalIdx}>
        <PayoutRecipientNum>{globalIdx + 1}</PayoutRecipientNum>
        <PayoutRecipientIdentifier>
          {displayName ? <PayoutRecipientName>{displayName}</PayoutRecipientName> : null}
          {displayAccount && <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            <PayoutRecipientPrimary>{shortAddress(displayAccount, 6)}</PayoutRecipientPrimary>
            <span onClick={e => e.stopPropagation()}>
              <CopyButton text={displayAccount} />
            </span>
          </div>}
        </PayoutRecipientIdentifier>
        <PayoutRecipientAmount>
          {roundAssetAmount(entry.currency, Number(entry.amount))} {entry.currency}
        </PayoutRecipientAmount>
        <PayoutRecipientStatus>
          <InlinePill $tone={payoutStatusTone(entry.status)}>{payoutStatusLabel(entry.status)}</InlinePill>
        </PayoutRecipientStatus>
      </PayoutRecipientDataRow>
    })}

    {hiddenCount > 0 && <PayoutRecipientOverflowRow>
      +{hiddenCount} more recipient{hiddenCount !== 1 ? 's' : ''} —{' '}
      <Link to={viewAllHref} style={{ color: RADOM_COLORS.RADOM_BLUE }}>view all</Link>
    </PayoutRecipientOverflowRow>}
  </PayoutRecipientGrid>
}

export default PayoutRecipients